/**
 * The finance overview's `summary` block (IBA-117).
 *
 * The overview envelope types `summary` as `Record<string, unknown>` because every module fills it
 * differently. These are the keys FINANCE puts there, and the shape they carry.
 *
 * `totalCash` exists because the dashboard used to sum a *page* of accounts in the browser: a workspace
 * with more than one page of accounts silently displayed a total that was too small. A total is the
 * backend's job — it is computed here over EVERY account in scope, with no pagination.
 */
import type { MoneyString } from "./money";

export enum FinanceOverviewSummaryKey {
  Currency = "currency",
  TotalCash = "totalCash",
  AccountCount = "accountCount",
  NonZeroAccountCount = "nonZeroAccountCount",
  HealthRatioBp = "healthRatioBp",
}

export interface FinanceOverviewSummary {
  /** The accounting currency of the scope (branch, or the system branch for parish). */
  currency: string | null;
  /**
   * Sum of the CURRENT balance of every active account in scope. Never paginated.
   *
   * A canonical decimal string at the currency's scale (IBA-116) — `"1500.00"` in USD, `"1500"` in RWF. Money
   * does not cross the wire as a `number`: IEEE-754 cannot hold most decimal fractions, so a float is a lossy
   * container for an amount that has to reconcile with a bank statement.
   */
  totalCash: MoneyString;
  /** Every active account in scope — the denominator behind `totalCash`. */
  accountCount: number;
  /** Active accounts in scope whose current balance is not zero. A zero account cannot move `totalCash`. */
  nonZeroAccountCount: number;
  /**
   * What share of the period's income the scope actually kept: `net / income`, in integer BASIS POINTS
   * (0–10000), clamped. A percentage is a computed measure like any other, so the backend computes it — the
   * summary page used to divide two card values in the browser and round the quotient itself.
   */
  healthRatioBp: number;
}
